import React from 'react';
import { localStateStore } from '@/utils/localStateStore';

type ErrorBoundaryState = {
  hasError: boolean;
  reloadBlocked: boolean;
  reloadDelayMs: number;
};

export class ErrorBoundary extends React.Component<React.PropsWithChildren, ErrorBoundaryState> {
  private reloadTimer: number | null = null;
  private successTimer: number | null = null;
  private readonly reloadStateKey = 'veeqRendererErrorReloadState';
  private readonly windowMs = 10 * 60 * 1000;
  private readonly maxReloads = 3;

  state: ErrorBoundaryState = {
    hasError: false,
    reloadBlocked: false,
    reloadDelayMs: 0
  };

  static getDerivedStateFromError(): Partial<ErrorBoundaryState> {
    return { hasError: true };
  }

  componentDidMount() {
    this.successTimer = window.setTimeout(() => {
      localStateStore.remove(this.reloadStateKey);
    }, 30000);
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[ErrorBoundary] Renderer hatasi yakalandi:', error, info);
    const now = Date.now();
    const previous = localStateStore.getJson<{ count: number; firstAt: number }>(
      this.reloadStateKey,
      { count: 0, firstAt: now }
    ) || { count: 0, firstAt: now };
    const isFreshWindow = now - previous.firstAt <= this.windowMs;
    const nextState = {
      firstAt: isFreshWindow ? previous.firstAt : now,
      count: isFreshWindow ? previous.count + 1 : 1
    };

    localStateStore.setJson(this.reloadStateKey, nextState);

    if (nextState.count > this.maxReloads) {
      this.setState({ reloadBlocked: true, reloadDelayMs: 0 });
      return;
    }

    const delay = Math.min(5000 * (2 ** (nextState.count - 1)), 30000);
    this.setState({ reloadBlocked: false, reloadDelayMs: delay });

    this.reloadTimer = window.setTimeout(() => {
      window.location.reload();
    }, delay);
  }

  componentWillUnmount() {
    if (this.reloadTimer) {
      window.clearTimeout(this.reloadTimer);
      this.reloadTimer = null;
    }

    if (this.successTimer) {
      window.clearTimeout(this.successTimer);
      this.successTimer = null;
    }
  }

  private reload = () => {
    localStateStore.remove(this.reloadStateKey);
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-950 text-white px-6">
          <div className="max-w-md w-full rounded-lg border border-white/10 bg-white/5 p-6 text-center">
            <h1 className="text-xl font-semibold mb-2">Player yeniden baslatilmali</h1>
            <p className="text-sm text-white/70 mb-5">
              {this.state.reloadBlocked
                ? 'Beklenmeyen ekran hatasi tekrar etti. Otomatik yenileme durduruldu.'
                : `Beklenmeyen bir ekran hatasi yakalandi. Muzik sistemi ${Math.ceil(this.state.reloadDelayMs / 1000)} saniye icinde tekrar yuklenecek.`}
            </p>
            <button
              type="button"
              onClick={this.reload}
              className="rounded-md bg-yellow-400 px-4 py-2 text-sm font-semibold text-black"
            >
              Yeniden Yukle
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
